/**
 * Nexora - Authentication Context
 *
 * Global state management for user authentication.
 * Provides a centralized way to manage and access auth state across the app.
 *
 * TODO: Integration with real Supabase when credentials are added
 */

import { AuthService, clearPlaceholderAuthStorageIfForbidden } from './authService.js'
import { STORAGE_KEYS } from '../constants/storageKeys.js'
import { getUserDisplayName as resolveUserDisplayName } from './userDisplayName.js'
import { initialAuthRedirect } from '../supabase.js'

/**
 * AuthContext - Simple event-based state management
 * Similar to React Context but for vanilla JavaScript
 *
 * Usage:
 * - AuthContext.subscribe(callback) - Listen to auth state changes
 * - AuthContext.getState() - Get current auth state
 * - AuthContext.signIn(email, password) - Sign in user
 * - AuthContext.signOut() - Sign out user
 */
export const AuthContext = {
  // Internal state
  _state: {
    user: null,
    session: null,
    isLoading: false,
    isAuthenticated: false,
    error: null,
    isPasswordRecovery: false
  },

  // Array of listeners to notify on state changes
  _listeners: [],
  _authUnsubscribe: null,
  _passwordRecoveryRedirectPending: false,

  /**
   * Subscribe to auth state changes
   * @param {Function} listener - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    this._listeners.push(listener)

    // Return unsubscribe function
    return () => {
      this._listeners = this._listeners.filter(l => l !== listener)
    }
  },

  /**
   * Notify all listeners of state change
   * @private
   */
  _notifyListeners() {
    this._listeners.forEach(listener => listener(this._state))
  },

  /**
   * Get current auth state
   */
  getState() {
    return { ...this._state }
  },

  /**
   * Set user session
   * @private
   */
  async _setUser(user, session = null) {
    this._state.user = user
    this._state.session = session
    this._state.isAuthenticated = !!(user && session)

    // Also store in placeholder session storage
    if (user && session) {
      AuthService.storeSessionPlaceholder(user, session)
    }

    this._notifyListeners()
  },

  /**
   * Refresh cloud budget state after login/session restore.
   * Non-blocking: the app keeps using local data if cloud sync is unavailable.
   * @private
   */
  _syncSupabaseToLocalAfterLogin() {
    setTimeout(async () => {
      try {
        const initializeCurrentBudgetMonth = window.initializeCurrentBudgetMonth
        if (typeof initializeCurrentBudgetMonth === 'function') {
          await initializeCurrentBudgetMonth()
        }
        if (typeof window.loadMonth === 'function') await window.loadMonth()
        if (typeof window.buildHistory === 'function') window.buildHistory()
        if (typeof window.updateAll === 'function') window.updateAll()

        if (window.UserAppSettingsService && typeof window.UserAppSettingsService.syncAllAppSettings === 'function') {
          await window.UserAppSettingsService.syncAllAppSettings()
        }
        if (window.UserAppSettingsService && typeof window.UserAppSettingsService.syncCloudSettingToLocal === 'function') {
          await window.UserAppSettingsService.syncCloudSettingToLocal(STORAGE_KEYS.goals)
        }
        if (window.GoalsPage && typeof window.GoalsPage.render === 'function') {
          await window.GoalsPage.render()
        }
        if (window.GoalsPage && typeof window.GoalsPage.renderAnalytics === 'function') {
          await window.GoalsPage.renderAnalytics()
        }
        if (typeof window.renderRecurringIncomeSettings === 'function') await window.renderRecurringIncomeSettings()
        if (typeof window.renderBillScheduleSettings === 'function') await window.renderBillScheduleSettings()
        if (typeof window.renderDebts === 'function') window.renderDebts()
        // Removed redundant loadMonth() call here (already called above)
        if (typeof window.updateAll === 'function') window.updateAll()

        // Ensure custom categories UI is rendered after session restore
        if (typeof window.renderCustomBudgetRows === 'function') {
          await window.renderCustomBudgetRows()
        }

        if (typeof window.refreshCategoryLabels === 'function') {
          await window.refreshCategoryLabels()
        }

        if (typeof window.updateAll === 'function') window.updateAll()
      } catch (error) {
        console.error('[Phase2D] sync error:', error)
      }
    }, 0)
  },

  /**
   * Set error
   * @private
   */
  _setError(error) {
    this._state.error = error
    this._notifyListeners()
  },

  /**
   * Set loading state
   * @private
   */
  _setLoading(isLoading) {
    this._state.isLoading = isLoading
    this._notifyListeners()
  },

  /**
   * Keep the local context aligned with Supabase token and session events.
   * @private
   */
  _setupAuthListener() {
    if (typeof this._authUnsubscribe === 'function') {
      this._authUnsubscribe()
    }

    this._authUnsubscribe = AuthService.subscribeToAuthChanges(({ event, user, session }) => {
      if (event === 'SIGNED_OUT') {
        this._state.user = null
        this._state.session = null
        this._state.isAuthenticated = false
        this._state.error = null
        this._state.isPasswordRecovery = false
        this._passwordRecoveryRedirectPending = false
        AuthService.clearSessionPlaceholder()
        this._notifyListeners()
        return
      }

      // PASSWORD_RECOVERY - session de récupération validée par le SDK
      // C'est le SEUL signal fiable d'une récupération de mot de passe valide
      if (event === 'PASSWORD_RECOVERY') {
        this._passwordRecoveryRedirectPending = false
        this._state.isPasswordRecovery = true
        this._state.user = user
        this._state.session = session
        this._state.isAuthenticated = true
        this._notifyListeners()
        return
      }

      if (!user) return

      const previousUserId = this._state.user?.id || null
      this._state.user = user
      this._state.session = session || null
      this._state.isAuthenticated = !!(user && session)
      this._state.error = null

      if (this._passwordRecoveryRedirectPending && user && session) {
        this._state.isPasswordRecovery = true
        this._passwordRecoveryRedirectPending = false
      }

      this._notifyListeners()

      // BLOQUAGE: Si mode récupération activé, ne jamais synchroniser ni naviguer
      if (this._state.isPasswordRecovery) {
        return
      }

      // Comportement normal SIGNED_IN hors récupération
      if (event === 'SIGNED_IN' && previousUserId !== user.id && !this._state.isLoading) {
        this._syncSupabaseToLocalAfterLogin()
      }
    })
  },

  /**
   * Initialize auth context - Check if user is already logged in
   * Called when app starts
   */
  async init() {
    this._setLoading(true)

    try {
      // TODO: When Supabase is configured:
      // - Remove placeholder logic
      // - Set up real auth listener with supabase.auth.onAuthStateChange()

      // Clear placeholder storage if not allowed before any restoration
      clearPlaceholderAuthStorageIfForbidden()

      // Recovery intent was captured synchronously before Supabase client
      // initialization. Keep it pending until a real session proves the link.
      this._passwordRecoveryRedirectPending =
        initialAuthRedirect.isPasswordRecovery

      // Subscribe before explicit session restoration. If Supabase already
      // consumed PASSWORD_RECOVERY, INITIAL_SESSION/SIGNED_IN can still
      // complete the pending recovery transition.
      this._setupAuthListener()

      // Restore the real Supabase session.
      const { user } = await AuthService.getCurrentUser()
      const { session } = await AuthService.getSession()

      if (user && session) {
        this._state.user = user
        this._state.session = session
        this._state.isAuthenticated = true
        this._state.error = null

        // Fallback when session restoration wins the race before the auth
        // listener emits its first event.
        if (this._passwordRecoveryRedirectPending) {
          this._state.isPasswordRecovery = true
          this._passwordRecoveryRedirectPending = false
        }

        // A recovery session must never bootstrap the normal dashboard data.
        if (!this._state.isPasswordRecovery) {
          this._syncSupabaseToLocalAfterLogin()
        }
      } else {
        this._state.user = null
        this._state.session = null
        this._state.isAuthenticated = false
      }
    } catch (error) {
      console.error('❌ AuthContext init error:', error)
      this._state.error = error
    } finally {
      this._setLoading(false)
    }
  },

  /**
   * Sign up new user
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} username - User display name
   */
  async signUp(email, password, username) {
    this._setLoading(true)
    this._setError(null)

    try {

      const { user, session, error } = await AuthService.signUp(email, password, username)

      if (error) {
        this._setError(error)
        console.error('❌ SignUp failed:', error)
        return { user: null, error }
      }
      this._setUser(user, session)
      return { user, session, error: null }
    } catch (error) {
      console.error('❌ SignUp exception:', error)
      this._setError(error)
      return { user: null, error }
    } finally {
      this._setLoading(false)
    }
  },

  /**
   * Sign in user
   * @param {string} email - User email
   * @param {string} password - User password
   */
  async signIn(email, password) {
    this._setLoading(true)
    this._setError(null)

    try {

      const { user, session, error } = await AuthService.signIn(email, password)

      if (error) {
        this._setError(error)
        console.error('❌ SignIn failed:', error)
        return { user: null, session: null, error }
      }
      await this._setUser(user, session)
      this._syncSupabaseToLocalAfterLogin()
      return { user, session, error: null }
    } catch (error) {
      console.error('❌ SignIn exception:', error)
      this._setError(error)
      return { user: null, session: null, error }
    } finally {
      this._setLoading(false)
    }
  },

  /**
   * Sign out user
   */
  async signOut() {
    this._setLoading(true)
    this._setError(null)

    try {

      const { error } = await AuthService.signOut()

      if (error) {
        this._setError(error)
        console.error('❌ SignOut failed:', error)
        return { error }
      }

      // Clear user and session
      this._state.user = null
      this._state.session = null
      this._state.isAuthenticated = false

      // Clear placeholder storage
      AuthService.clearSessionPlaceholder()

      this._notifyListeners()
      return { error: null }
    } catch (error) {
      console.error('❌ SignOut exception:', error)
      this._setError(error)
      return { error }
    } finally {
      this._setLoading(false)
    }
  },

  /**
   * Get current user
   */
  getCurrentUser() {
    return this._state.user
  },

  /**
   * Get current session
   */
  getCurrentSession() {
    return this._state.session
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this._state.isAuthenticated
  },

  /**
   * Set password recovery mode
   * @param {boolean} enabled - Whether to enable password recovery mode
   */
  setPasswordRecoveryMode(enabled) {
    this._state.isPasswordRecovery = enabled
    this._notifyListeners()
  },

  /**
   * Exit password recovery mode
   */
  exitPasswordRecoveryMode() {
    this._state.isPasswordRecovery = false
    this._notifyListeners()
  },

  /**
   * Check if in password recovery mode
   */
  isPasswordRecoveryMode() {
    return this._state.isPasswordRecovery
  },

  /**
   * Update user password
   * @param {string} newPassword - New password
   * @returns {Promise<{error}>}
   */
  async updatePassword(newPassword) {
    this._setLoading(true)
    this._setError(null)

    try {
      const { error } = await AuthService.updatePassword(newPassword)

      if (error) {
        this._setError(error)
        console.error('❌ UpdatePassword failed:', error)
        return { error }
      }

      return { error: null }
    } catch (error) {
      console.error('❌ UpdatePassword exception:', error)
      this._setError(error)
      return { error }
    } finally {
      this._setLoading(false)
    }
  },

  /**
   * Nom affiché (prénom / displayName / extrait du nom complet — jamais email ni handle).
   */
  getUserDisplayName() {
    return resolveUserDisplayName(this._state.user)
  }
}

export default AuthContext
